export const POPULAR_EMAIL_DOMAINS = [
    'gmail.com',
    'seznam.cz',
    'web.de',
    'gmx.de',
    'arste.com',
    'hotmail.com',
    'email.cz',
    'icloud.com',
    'centrum.cz',
    'yahoo.com',
    't-online.de',
    'googlemail.com',
    'yahoo.de',
    'wp.pl',
    'gmx.net',
    'hotmail.de',
    'outlook.com',
    'privaterelay.appleid.com',
    'post.cz',
    'leonardo-hotels.com',
    'gmx.at',
    'volny.cz',
    'freenet.de',
    'outlook.de',
    'o2.pl',
    'me.com',
    'aol.com',
    'fridayhotel.cz',
    'interia.pl',
    'live.de',
    'atlas.cz',
    'arcor.de',
    'op.pl',
    'mail.ru',
    'esthe-laser.cz',
    'mrparkit.com',
    'online.de',
    'occidentalhotels.com',
    'sovereignhotel.cz',
    'hotmail.fr',
    'csas.cz',
    'tiscali.cz',
    'casaleproject.cz',
    'naver.com',
    'live.com',
    'poczta.onet.pl',
    'onet.pl',
    'outlook.cz',
    'live.nl',
    'mail.de',
    'hotelalwyn.cz',
    'posteo.de',
    'ukr.net',
    'yahoo.fr',
    'msn.com',
    'fichtner.cz',
    'centrum.sk',
    'vp.pl',
    'orange.fr',
    'yahoo.co.uk',
    'eon.com',
    'protonmail.com',
    'guest.booking.com',
    'hotmail.it',
    'tlen.pl',
    'ymail.com',
    'poczta.fm',
    'onet.eu',
    'mallgroup.com',
    'hotmail.co.uk',
    'hhotels.cz',
    'yandex.ru',
    'aol.de',
    'libero.it',
    'gmx.ch',
    'incomind.cz',
    'bluewin.ch',
    'wmfts.com',
    'abv.bg',
    'bidli.cz',
    'aon.at',
    'fruitisimogroup.com',
    'formfactory.cz',
    'kaufner.net',
    'azet.sk',
    'allegro.com',
    'hotelapollon.cz',
    'mh-dane.cz',
    'drmax.cz',
    'kabelmail.de',
    'yahoo.it',
    'residencespalena.cz',
    'jerome.cz',
    'dtgroup.cz',
    'rosmart.cz',
    'learningtoys.cz',
    'hotmail.nl',
    'free.fr',
    'mailbox.org',
] as const

export type EmailDomainSuggestion = {
    source: string
    correctedEmail: string
}

const MAX_EMAIL_DOMAIN_EDIT_DISTANCE = 1

function damerauLevenshteinDistance(source: string, target: string): number {
    const distances = Array.from(
        { length: source.length + 1 },
        () => Array<number>(target.length + 1).fill(0),
    )

    for (let sourceIndex = 0; sourceIndex <= source.length; sourceIndex += 1) {
        distances[sourceIndex][0] = sourceIndex
    }

    for (let targetIndex = 0; targetIndex <= target.length; targetIndex += 1) {
        distances[0][targetIndex] = targetIndex
    }

    for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
        for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
            const substitutionCost = source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1

            distances[sourceIndex][targetIndex] = Math.min(
                distances[sourceIndex - 1][targetIndex] + 1,
                distances[sourceIndex][targetIndex - 1] + 1,
                distances[sourceIndex - 1][targetIndex - 1] + substitutionCost,
            )

            const isAdjacentTransposition = sourceIndex > 1
                && targetIndex > 1
                && source[sourceIndex - 1] === target[targetIndex - 2]
                && source[sourceIndex - 2] === target[targetIndex - 1]

            if (isAdjacentTransposition) {
                distances[sourceIndex][targetIndex] = Math.min(
                    distances[sourceIndex][targetIndex],
                    distances[sourceIndex - 2][targetIndex - 2] + 1,
                )
            }
        }
    }

    return distances[source.length][target.length]
}

export function getEmailDomainSuggestion(value: string): string | null {
    const email = value.trim()
    const atIndex = email.indexOf('@')

    if (
        atIndex <= 0
        || atIndex !== email.lastIndexOf('@')
        || /\s/.test(email)
    ) {
        return null
    }

    const localPart = email.slice(0, atIndex)
    const domain = email.slice(atIndex + 1)
    const normalizedDomain = domain.toLowerCase()

    if (
        normalizedDomain === ''
        || !normalizedDomain.includes('.')
        || POPULAR_EMAIL_DOMAINS.some(candidate => candidate === normalizedDomain)
    ) {
        return null
    }

    let closestDomain: string | null = null
    let closestDistance = MAX_EMAIL_DOMAIN_EDIT_DISTANCE + 1

    for (const candidate of POPULAR_EMAIL_DOMAINS) {
        if (Math.abs(candidate.length - normalizedDomain.length) > MAX_EMAIL_DOMAIN_EDIT_DISTANCE) {
            continue
        }

        const distance = damerauLevenshteinDistance(normalizedDomain, candidate)

        if (
            distance <= MAX_EMAIL_DOMAIN_EDIT_DISTANCE
            && distance < closestDistance
        ) {
            closestDomain = candidate
            closestDistance = distance
        }
    }

    return closestDomain ? `${localPart}@${closestDomain}` : null
}
